import { edtEntryInputSchema } from '@iculogbook/shared';
import type { EdtEntryInput } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { useToast } from '../components/index.ts';
import { edtRepository } from '../db/repositories/edt.ts';
import { EdtForm } from '../features/log/EdtForm.tsx';
import type { FormErrors } from '../features/log/ProcedureForm.tsx';
import { buildEdtEntryInput, edtValuesFrom, initialValues } from '../features/log/edtForm.ts';
import type { EdtFormValues } from '../features/log/edtForm.ts';

/**
 * Logging Educational Development Time: teaching given or attended, a clinic,
 * some reading, a project or an audit.
 *
 * Local, like every other write path in this app. The save writes to Dexie and
 * returns; the sync engine drains the outbox afterwards, whenever it can.
 */
function LogEdtRouteForm() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { edtId } = useParams();
  const location = useLocation();

  const userId = user?.id ?? null;
  const editing = edtId !== undefined;

  /** A duplicate arrives prefilled through the router, never through the URL. */
  const prefill = (location.state as { prefill?: EdtEntryInput } | null)?.prefill;

  const stored = useLiveQuery(
    async () =>
      userId === null || edtId === undefined
        ? null
        : ((await edtRepository.get(userId, edtId)) ?? null),
    [userId, edtId],
  );

  const [values, setValues] = useState<EdtFormValues | null>(() =>
    editing ? null : prefill !== undefined ? edtValuesFrom(prefill) : initialValues(new Date()),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  /*
   * Seed once, then leave the form alone: a live query must never overwrite
   * something half-typed.
   */
  const [seeded, setSeeded] = useState(false);
  if (editing && !seeded && values === null && stored !== undefined && stored !== null) {
    setSeeded(true);
    setValues(edtValuesFrom(stored));
  }

  async function save(andDuplicate = false): Promise<void> {
    if (values === null || userId === null) return;

    const input = buildEdtEntryInput(values);
    if (input === null) {
      setErrors({
        ...(values.activity === null ? { activity: 'Choose what kind of activity this was.' } : {}),
        ...(values.title.trim() === '' ? { title: 'Give this a short title.' } : {}),
      });
      return;
    }

    // The shared schema is the only validator. Anything it rejects is a bug in
    // the form rather than something the user did, but it is reported rather
    // than swallowed, because writing an invalid record would be worse.
    const parsed = edtEntryInputSchema.safeParse(input);
    if (!parsed.success) {
      const collected: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && collected[field] === undefined) {
          collected[field] = issue.message;
        }
      }
      setErrors(collected);
      return;
    }

    setSaving(true);

    try {
      let savedId = edtId;

      if (editing && edtId !== undefined) {
        await edtRepository.update(userId, edtId, parsed.data);
        showToast({ message: 'Entry updated.', tone: 'success' });
      } else {
        const saved = await edtRepository.create(userId, parsed.data);
        savedId = saved.id;

        showToast({
          message: 'Entry saved.',
          tone: 'success',
          actions: [
            {
              label: 'Undo',
              onAction: () => {
                void edtRepository.softDelete(userId, saved.id);
                showToast({ message: 'Entry removed.' });
              },
            },
          ],
        });
      }

      const copy =
        andDuplicate && savedId !== undefined
          ? await edtRepository.duplicate(userId, savedId)
          : undefined;

      void navigate(copy === undefined ? '/entries' : '/log/edt', {
        ...(copy === undefined ? {} : { state: { prefill: copy } }),
      });
    } finally {
      setSaving(false);
    }
  }

  if (editing && stored === null) {
    return <p className="text-sm text-muted">That entry no longer exists.</p>;
  }

  if (values === null) return null;

  return (
    <EdtForm
      values={values}
      onChange={setValues}
      onSave={() => void save()}
      onSaveAndDuplicate={() => void save(true)}
      saving={saving}
      errors={errors}
      editing={editing}
    />
  );
}

/**
 * Saving and duplicating navigates to this same route with a fresh prefill.
 * Keying on the location gives that navigation a new instance, so the form
 * seeds from the copy instead of keeping what was typed into the last one.
 */
export function LogEdtRoute() {
  return <LogEdtRouteForm key={useLocation().key} />;
}
